import { Box, Heading, LinkBox, Stack, Text, VStack } from "@biblioteksentralen/react";
import {
  digitalLibraryServiceCategories,
  isBibliofilAppService,
  isBiblioteketAppService,
  isDigitalLibraryCustomService,
} from "@libry-content/common";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useSitePalette } from "../../utils/useSitePalette";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImageFillAvailableSpace } from "../images/SanityImageFillAvailableSpace";
import { ServiceCategoryIcon } from "./ServiceCategoryIcon";
import { ResolvedDigitalLibraryServiceSummary } from "@libry-content/common";
import { useDigitalServiceColor } from "./useDigitalServiceColor";

const layoutBreakpoint = "sm";

type LibraryAppPreviewProps = {
  digitalLibraryService: ResolvedDigitalLibraryServiceSummary;
};

export const LibraryAppPreview = ({ digitalLibraryService }: LibraryAppPreviewProps) => {
  const { ts } = useTranslation();
  const { category } = digitalLibraryService;
  const href = getPath(digitalLibraryService);
  const palette = useSitePalette();
  const iconColor = useDigitalServiceColor(digitalLibraryService);

  if (
    !isBibliofilAppService(digitalLibraryService) &&
    !isBiblioteketAppService(digitalLibraryService) &&
    !isDigitalLibraryCustomService(digitalLibraryService)
  ) {
    console.error(`Found library app service of type "${digitalLibraryService?._type}"`);
    return null;
  }

  if (!href || !category) return null;

  return (
    <LinkBox role="group" key={digitalLibraryService._type} marginBottom={{ base: "1rem", lg: "0" }}>
      <Box
        {...palette.colors.card.css}
        position="relative"
        borderRadius="lg"
        overflow="hidden"
        padding={{ base: "1rem", [layoutBreakpoint]: "0" }}
      >
        <Stack
          alignItems="flex-start"
          spacing="1.5rem"
          position="relative"
          flexDirection={{ base: "column", [layoutBreakpoint]: "row" }}
          width={{ base: "fit-content", [layoutBreakpoint]: undefined }}
          margin={{ base: "0 auto", [layoutBreakpoint]: "0" }}
        >
          <Heading as="h2" fontSize="lg" textAlign="left" display={{ base: "block", [layoutBreakpoint]: "none" }}>
            {ts(digitalLibraryServiceCategories[category])}
          </Heading>

          {!!digitalLibraryService.image?.asset && (
            <Box
              position="relative"
              width="100%"
              alignSelf="stretch"
              minHeight="10rem"
              borderRadius={{ base: "lg", [layoutBreakpoint]: "none" }}
              overflow="hidden"
              display="grid" // https://stackoverflow.com/questions/8468066/child-inside-parent-with-min-height-100-not-inheriting-height
            >
              <SanityImageFillAvailableSpace
                resolution={1080}
                image={digitalLibraryService.image}
                hideCaption
                _groupHover={{ transform: "unset" }}
                borderRadius="none"
              />
            </Box>
          )}

          {!digitalLibraryService.image?.asset && (
            <Box
              position="relative"
              borderRadius={{ base: "lg", sm: "none" }}
              overflow="hidden"
              padding="1rem"
              {...iconColor?.css}
            >
              <ServiceCategoryIcon width="100%" height="100%" category={category} background="none" />
            </Box>
          )}

          <VStack
            alignItems="flex-start"
            spacing="0"
            padding={{ base: "0", [layoutBreakpoint]: "2rem 1rem 2rem 0" }}
            justifyContent="center"
            margin="auto 0"
          >
            <Heading as="h3" fontSize="xl" marginBottom="0.75rem" display="block">
              <InternalLinkOverlay href={href}>{ts(digitalLibraryService?.name)}</InternalLinkOverlay>
            </Heading>
            <Text>{ts(digitalLibraryService.teaser)}</Text>
            <Edit doc={digitalLibraryService} marginTop="0.5rem" />
          </VStack>
        </Stack>
      </Box>
    </LinkBox>
  );
};
